import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import {
  Bot,
  Wrench,
  GitFork,
  Hand,
  CircleDot,
  Square,
  Stethoscope,
  CreditCard,
  Calendar,
  Shield,
  FileText,
  Database,
  Mail,
  Bell,
  ClipboardList,
  Zap,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { ContextBadge, type ContextMeta } from "./ContextBadge";
import { AVAILABLE_CONTEXTS } from "./ContextPicker";

const AGENT_TYPE_BADGES: Record<string, string> = {
  supervisor: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  worker: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  router: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  tool_executor: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  general: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
};

/** Execution state classes for node highlighting during runs */
function execClasses(data: Record<string, unknown>) {
  if (data.__execState === "active")
    return "ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse shadow-lg shadow-primary/20";
  if (data.__execState === "completed")
    return "opacity-75 ring-1 ring-emerald-500/40";
  if (data.__execState === "error")
    return "ring-2 ring-destructive ring-offset-2 ring-offset-background";
  return "";
}

function ExecBadge({ state }: { state: string | undefined }) {
  if (!state || state === "idle") return null;
  if (state === "active")
    return <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary animate-ping" />;
  if (state === "completed")
    return (
      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white text-[8px]">
        ✓
      </span>
    );
  if (state === "error")
    return (
      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white text-[8px]">
        ✕
      </span>
    );
  return null;
}

export const AgentFlowNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Agent";
  const agentType = (data.agentType as string) ?? "general";
  const modelId = (data.modelId as string) ?? "";
  const modelLabel = modelId.includes("opus")
    ? "Opus"
    : modelId.includes("sonnet")
      ? "Sonnet"
      : modelId.includes("haiku")
        ? "Haiku"
        : modelId.includes("nova")
          ? "Nova"
          : "Model";

  const attachedContextIds = (data.contexts as string[]) ?? [];
  const attachedContexts: ContextMeta[] = attachedContextIds
    .map((id) => AVAILABLE_CONTEXTS.find((c) => c.id === id))
    .filter(Boolean) as ContextMeta[];

  return (
    <div
      className={cn(
        "relative rounded-xl border-2 bg-card shadow-sm min-w-[180px] transition-all",
        selected ? "border-primary shadow-md" : "border-border",
        execClasses(data as Record<string, unknown>),
      )}
    >
      <ExecBadge state={data.__execState as string | undefined} />
      <Handle type="target" position={Position.Top} className="!bg-primary !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Bot className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{name}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span
                className={cn(
                  "rounded-full px-1.5 py-px text-[9px] font-medium uppercase tracking-wider",
                  AGENT_TYPE_BADGES[agentType] ?? AGENT_TYPE_BADGES.general,
                )}
              >
                {agentType}
              </span>
              <span className="text-[10px] text-muted-foreground">{modelLabel}</span>
            </div>
            {attachedContexts.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {attachedContexts.map((ctx) => (
                  <ContextBadge key={ctx.id} context={ctx} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-primary !w-3 !h-3 !border-2 !border-background" />
    </div>
  );
});
AgentFlowNode.displayName = "AgentFlowNode";

/** Maps tool names to healthcare-specific icons and colors */
const TOOL_STYLES: Record<string, { icon: React.ReactNode; bg: string; text: string; border: string; handle: string }> = {
  "EHR Lookup": { icon: <Database className="h-4 w-4" />, bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500", handle: "!bg-emerald-500" },
  "Push to EHR": { icon: <Database className="h-4 w-4" />, bg: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-600 dark:text-teal-400", border: "border-teal-500", handle: "!bg-teal-500" },
  "Payer Query": { icon: <CreditCard className="h-4 w-4" />, bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-600 dark:text-blue-400", border: "border-blue-500", handle: "!bg-blue-500" },
  "Send Email": { icon: <Mail className="h-4 w-4" />, bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-600 dark:text-orange-400", border: "border-orange-500", handle: "!bg-orange-500" },
  "Notify Staff": { icon: <Bell className="h-4 w-4" />, bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-600 dark:text-yellow-400", border: "border-yellow-500", handle: "!bg-yellow-500" },
  "Audit Log": { icon: <ClipboardList className="h-4 w-4" />, bg: "bg-zinc-100 dark:bg-zinc-800", text: "text-zinc-600 dark:text-zinc-400", border: "border-zinc-500", handle: "!bg-zinc-500" },
  "Schedule Action": { icon: <Calendar className="h-4 w-4" />, bg: "bg-violet-100 dark:bg-violet-900/30", text: "text-violet-600 dark:text-violet-400", border: "border-violet-500", handle: "!bg-violet-500" },
  "Document Gen": { icon: <FileText className="h-4 w-4" />, bg: "bg-cyan-100 dark:bg-cyan-900/30", text: "text-cyan-600 dark:text-cyan-400", border: "border-cyan-500", handle: "!bg-cyan-500" },
};

const DEFAULT_TOOL_STYLE = { icon: <Wrench className="h-4 w-4" />, bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500", handle: "!bg-emerald-500" };

export const ToolFlowNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Tool";
  const style = TOOL_STYLES[name] ?? DEFAULT_TOOL_STYLE;
  return (
    <div
      className={cn(
        "relative rounded-xl border-2 bg-card shadow-sm min-w-[150px] transition-all",
        selected ? `${style.border} shadow-md` : "border-border",
        execClasses(data as Record<string, unknown>),
      )}
    >
      <ExecBadge state={data.__execState as string | undefined} />
      <Handle type="target" position={Position.Top} className={cn("!w-3 !h-3 !border-2 !border-background", style.handle)} />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", style.bg, style.text)}>
          {style.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          {name in TOOL_STYLES && (
            <p className="text-[10px] text-muted-foreground">Healthcare Tool</p>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className={cn("!w-3 !h-3 !border-2 !border-background", style.handle)} />
    </div>
  );
});
ToolFlowNode.displayName = "ToolFlowNode";

export const RouterFlowNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Router";
  return (
    <div
      className={cn(
        "relative rounded-xl border-2 bg-card shadow-sm min-w-[150px] transition-all",
        selected ? "border-amber-500 shadow-md" : "border-border",
        execClasses(data as Record<string, unknown>),
      )}
    >
      <ExecBadge state={data.__execState as string | undefined} />
      <Handle type="target" position={Position.Top} className="!bg-amber-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
          <GitFork className="h-4 w-4" />
        </div>
        <p className="text-sm font-semibold truncate">{name}</p>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-amber-500 !w-3 !h-3 !border-2 !border-background" />
    </div>
  );
});
RouterFlowNode.displayName = "RouterFlowNode";

export const InterruptFlowNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Human Review";
  return (
    <div
      className={cn(
        "relative rounded-xl border-2 border-dashed bg-card shadow-sm min-w-[150px] transition-all",
        selected ? "border-orange-500 shadow-md" : "border-border",
        execClasses(data as Record<string, unknown>),
      )}
    >
      <ExecBadge state={data.__execState as string | undefined} />
      <Handle type="target" position={Position.Top} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
          <Hand className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">HITL Pause</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-background" />
    </div>
  );
});
InterruptFlowNode.displayName = "InterruptFlowNode";

export const StartFlowNode = memo(({ data, selected }: NodeProps) => (
  <div
    className={cn(
      "relative flex h-12 w-12 items-center justify-center rounded-full border-2 bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400 transition-all",
      selected ? "border-emerald-500 shadow-md" : "border-emerald-300 dark:border-emerald-700",
      execClasses((data ?? {}) as Record<string, unknown>),
    )}
  >
    <ExecBadge state={(data as Record<string, unknown>)?.__execState as string | undefined} />
    <CircleDot className="h-5 w-5" />
    <Handle type="source" position={Position.Bottom} className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-background" />
  </div>
));
StartFlowNode.displayName = "StartFlowNode";

export const EndFlowNode = memo(({ data, selected }: NodeProps) => (
  <div
    className={cn(
      "relative flex h-12 w-12 items-center justify-center rounded-full border-2 bg-zinc-50 text-zinc-500 dark:bg-zinc-900 dark:text-zinc-400 transition-all",
      selected ? "border-zinc-500 shadow-md" : "border-zinc-300 dark:border-zinc-700",
      execClasses((data ?? {}) as Record<string, unknown>),
    )}
  >
    <ExecBadge state={(data as Record<string, unknown>)?.__execState as string | undefined} />
    <Square className="h-5 w-5" />
    <Handle type="target" position={Position.Top} className="!bg-zinc-500 !w-3 !h-3 !border-2 !border-background" />
  </div>
));
EndFlowNode.displayName = "EndFlowNode";

/** Maps trigger event types to healthcare-specific icons and colors */
const TRIGGER_STYLES: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  trigger_clinical: { icon: <Stethoscope className="h-4 w-4" />, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/30" },
  trigger_claims: { icon: <CreditCard className="h-4 w-4" />, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30" },
  trigger_scheduling: { icon: <Calendar className="h-4 w-4" />, color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-100 dark:bg-violet-900/30" },
  trigger_prior_auth: { icon: <Shield className="h-4 w-4" />, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-100 dark:bg-amber-900/30" },
  trigger_referral: { icon: <FileText className="h-4 w-4" />, color: "text-cyan-600 dark:text-cyan-400", bg: "bg-cyan-100 dark:bg-cyan-900/30" },
};

const DEFAULT_TRIGGER_STYLE = { icon: <Zap className="h-4 w-4" />, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30" };

export const TriggerFlowNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Trigger";
  const eventType = (data.eventType as string) ?? "";
  const style = TRIGGER_STYLES[eventType] ?? DEFAULT_TRIGGER_STYLE;

  return (
    <div
      className={cn(
        "relative rounded-xl border-2 bg-card shadow-sm min-w-[160px] transition-all",
        selected ? "border-blue-500 shadow-md" : "border-border",
        execClasses(data as Record<string, unknown>),
      )}
    >
      <ExecBadge state={data.__execState as string | undefined} />
      {/* Green "live" indicator dot */}
      <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", style.bg, style.color)}>
          {style.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">Event Trigger</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-3 !h-3 !border-2 !border-background" />
    </div>
  );
});
TriggerFlowNode.displayName = "TriggerFlowNode";
